import React, { useState, useEffect } from "react";
import axios from "axios";
import { config } from "../../../Helpers/env";
import { LoadingGif } from "../../Utils/Loader";
import MainSnackbar from "../../Utils/Snackbar/SmsSnackbar";
import { Container } from "../../Utils/styledComponents";
import PageTitle from "../../Utils/smsTitle";
import { SubHeader } from "../../../Containers/SubHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faRocket, faHourglassHalf, faSkullCrossbones, faStethoscope,
  faFileExcel, faFilePdf, faDollarSign, faCoins, faChartLine
} from "@fortawesome/free-solid-svg-icons";
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../actions/snackbarActions";

const BackButton = ({ to, text }) => (
  <Link to={to}>
    <button className="btn btn-sm sms-btn px-4 py-2">
      <ArrowBackIcon fontSize="small" className="me-2" />
      Back to {text}
    </button>
  </Link>
);

const ServiceRevenueReport = ({ actions }) => {
  const today = new Date().toISOString().slice(0, 10);
  const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

  const [filters, setFilters] = useState({
    start_date: firstDayOfMonth,
    end_date: today,
  });

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState("all"); // default to All now

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);

      const res = await axios.get(`${config.smsUrl}/creport/generate_service_revenue_report_new`, { params });
      setReport(res.data);
      actions.snackbarActions.successSnackbar?.("Report Loaded Successfully");
    } catch (err) {
      console.error("Report error:", err);
      actions.snackbarActions.errorSnackbar?.("Failed to load report");
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [filters.start_date, filters.end_date]);

  const {
    summary = {},
    top_performing = [],
    medium_performing = [],
    dormant_services = [],
    all_services = [],
    generated_at,
    period
  } = report || {};

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : "-";
  const formatSSP = (amount) => `SSP ${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
  const formatUSD = (amount) => `$${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Choose data based on active tab
  const currentData = 
    activeTab === "top"    ? top_performing :
    activeTab === "medium" ? medium_performing :
    activeTab === "dormant" ? dormant_services :
    all_services; // "all" tab

  const totalRevenueApprox = (Number(summary.total_revenue_ssp || 0) + Number(summary.total_revenue_usd || 0)) / 100;

  return (
    <>
      <PageTitle title="Service Revenue Report" />
      <SubHeader title="Reports" subTitle="Performance • Revenue • Activity">
        <FontAwesomeIcon icon={faStethoscope} />
      </SubHeader>

      <Container className="mt-3">
        <MainSnackbar />

        {/* FILTER BAR */}
        <div className="card border-0 shadow mb-4 bg-light-subtle">
          <div className="card-body py-3">
            <div className="row align-items-end g-3">
              <div className="col-auto">
                <BackButton to="/reports/generalreports" text="Reports" />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1 text-muted">From</label>
                <input 
                  type="date" 
                  className="form-control form-control-sm shadow-sm" 
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} 
                />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1 text-muted">To</label>
                <input 
                  type="date" 
                  className="form-control form-control-sm shadow-sm" 
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} 
                />
              </div>
              <div className="col-auto">
                <button 
                  onClick={fetchReport} 
                  disabled={loading} 
                  className="btn btn-primary btn-sm px-4 shadow-sm"
                >
                  {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : null}
                  {loading ? "Loading..." : "Refresh"}
                </button>
              </div>
              <div className="col ms-auto text-end">
                <button className="btn btn-outline-success btn-sm me-2 shadow-sm">
                  <FontAwesomeIcon icon={faFileExcel} className="me-1" /> Excel
                </button>
                <button className="btn btn-outline-danger btn-sm shadow-sm">
                  <FontAwesomeIcon icon={faFilePdf} className="me-1" /> PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* SUMMARY CARDS */}
        <div className="row g-4 mb-5">
          <div className="col-lg-3 col-md-6">
            <div className="card h-100 shadow border-0 rounded-4 overflow-hidden">
              <div className="card-body text-white text-center p-4" style={{ background: "linear-gradient(135deg, #2ecc71, #27ae60)" }}>
                <FontAwesomeIcon icon={faRocket} size="3x" className="mb-3" />
                <h5 className="fw-bold mb-2">Top Performing</h5>
                <h2 className="display-6 fw-bolder">{summary.top_count || 0}</h2>
                <small className="opacity-75">Services</small>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <div className="card h-100 shadow border-0 rounded-4 overflow-hidden">
              <div className="card-body text-white text-center p-4" style={{ background: "linear-gradient(135deg, #f39c12, #e67e22)" }}>
                <FontAwesomeIcon icon={faHourglassHalf} size="3x" className="mb-3" />
                <h5 className="fw-bold mb-2">Medium</h5>
                <h2 className="display-6 fw-bolder">{summary.medium_count || 0}</h2>
                <small className="opacity-75">Services</small>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <div className="card h-100 shadow border-0 rounded-4 overflow-hidden">
              <div className="card-body text-white text-center p-4" style={{ background: "linear-gradient(135deg, #e74c3c, #c0392b)" }}>
                <FontAwesomeIcon icon={faSkullCrossbones} size="3x" className="mb-3" />
                <h5 className="fw-bold mb-2">Dormant</h5>
                <h2 className="display-6 fw-bolder">{summary.dormant_count || 0}</h2>
                <small className="opacity-75">30+ days inactive</small>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <div className="card h-100 shadow border-0 rounded-4 overflow-hidden">
              <div className="card-body text-white p-4" style={{ background: "linear-gradient(135deg, #3498db, #2980b9)" }}>
                <div className="text-center mb-3">
                  <FontAwesomeIcon icon={faChartLine} size="3x" />
                  <h5 className="fw-bold mt-2">Total Revenue</h5>
                </div>
                <div className="row g-2 text-center">
                  <div className="col-12">
                    <div className="bg-white bg-opacity-10 rounded p-2">
                      <small className="d-block opacity-75">SSP</small>
                      <div className="fs-4 fw-bold text-success">
                        <FontAwesomeIcon icon={faCoins} className="me-2" />
                        {formatSSP(summary.total_revenue_ssp)}
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="bg-white bg-opacity-10 rounded p-2">
                      <small className="d-block opacity-75">USD</small>
                      <div className="fs-4 fw-bold text-success">
                        <FontAwesomeIcon icon={faDollarSign} className="me-2" />
                        {formatUSD(summary.total_revenue_usd)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TABS */}
        <ul className="nav nav-tabs mb-4 border-0 flex-wrap">
          <li className="nav-item">
            <button 
              className={`nav-link fw-bold px-4 py-3 ${activeTab === "all" ? "active bg-primary text-white" : "bg-light text-dark"}`}
              onClick={() => setActiveTab("all")}
            >
              All Services ({all_services.length})
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link fw-bold px-4 py-3 ${activeTab === "top" ? "active bg-success text-white" : "bg-light text-dark"}`}
              onClick={() => setActiveTab("top")}
            >
              Top ({top_performing.length})
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link fw-bold px-4 py-3 ${activeTab === "medium" ? "active bg-warning text-dark" : "bg-light text-dark"}`}
              onClick={() => setActiveTab("medium")}
            >
              Medium ({medium_performing.length})
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link fw-bold px-4 py-3 ${activeTab === "dormant" ? "active bg-danger text-white" : "bg-light text-dark"}`}
              onClick={() => setActiveTab("dormant")}
            >
              Dormant ({dormant_services.length})
            </button>
          </li>
        </ul>

        {loading ? (
          <div className="text-center py-5"><LoadingGif /></div>
        ) : currentData.length === 0 ? (
          <div className="alert alert-info text-center py-5 border-0 shadow-sm">
            <h5 className="mb-3">No services found</h5>
            <p className="text-muted">Try a different date range or check if services were performed.</p>
          </div>
        ) : (
          <div className="card shadow border-0 rounded overflow-hidden">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-dark text-white">
                    <tr>
                      <th className="text-center">#</th>
                      <th>Service</th>
                      <th>Department</th>
                      <th className="text-center">Times Performed</th>
                      <th className="text-center">Quantity</th>
                      <th className="text-end">SSP Revenue</th>
                      <th className="text-end">USD Revenue</th>
                      <th>Last Performed</th>
                      <th className="text-center">Days Inactive</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((item, i) => (
                      <tr key={i} className="hover-light">
                        <td className="text-center fw-bold">{i + 1}</td>
                        <td className="fw-medium">{item.service_name}</td>
                        <td className="text-muted small">{item.department || "—"}</td>
                        <td className="text-center">{item.times_performed}</td>
                        <td className="text-center">{item.total_quantity}</td>
                        <td className="text-end fw-bold text-primary">
                          {formatSSP(item.revenue_ssp)}
                        </td>
                        <td className="text-end fw-bold text-success">
                          {formatUSD(item.revenue_usd)}
                        </td>
                        <td>{formatDate(item.last_performed_date)}</td>
                        <td className="text-center fw-bold text-warning">
                          {item.days_inactive >= 99999 ? "Never" : `${item.days_inactive}d`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-light fw-bold">
                    <tr>
                      <td colSpan="5" className="text-end pe-4">Grand Total:</td>
                      <td className="text-end text-primary">{formatSSP(summary.total_revenue_ssp)}</td>
                      <td className="text-end text-success">{formatUSD(summary.total_revenue_usd)}</td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="text-center mt-4 text-muted small">
          Generated: {generated_at || "-"} | Period: {period?.start_date || "-"} to {period?.end_date || "-"}
        </div>
      </Container>
    </>
  );
};

const mapDispatchToProps = dispatch => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) }
});

export default connect(null, mapDispatchToProps)(ServiceRevenueReport);